import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Search, Loader2, X, AlertTriangle, CheckCircle2, XCircle,
  Trash2, RotateCcw, ArrowLeft, ArrowRight,
} from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

const API = process.env.REACT_APP_BACKEND_URL;

const STEP_BLUE = '#0ea5e9';
const STEP_DONE = '#10b981';

const Step = ({ n, label, active, done }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10,
    color: done ? STEP_DONE : active ? STEP_BLUE : '#94a3b8',
    fontWeight: active || done ? 700 : 500, fontSize: 14,
  }}>
    <span style={{
      width: 30, height: 30, borderRadius: '50%',
      background: done ? STEP_DONE : active ? STEP_BLUE : '#e2e8f0',
      color: done || active ? '#fff' : '#64748b',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 13, fontWeight: 700,
      boxShadow: active || done ? '0 2px 8px rgba(14,165,233,0.25)' : 'none',
    }}>{done ? '✓' : n}</span>
    <span>{label}</span>
  </div>
);

const authHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session?.access_token || ''}`,
  };
};

// ImageCard — klickbar med röd overlay vid markering för borttagning
const ImageCard = ({ img, badge, badgeColor, selected, onToggle }) => (
  <div
    data-testid={`img-card-${img.key}`}
    onClick={onToggle}
    style={{
      position: 'relative', cursor: 'pointer',
      background: '#fff', borderRadius: 12, overflow: 'hidden',
      border: selected ? '2px solid #dc2626' : '1px solid #e5e7eb',
      boxShadow: selected
        ? '0 0 0 3px rgba(220,38,38,0.15)'
        : '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
      transition: 'all 0.15s',
    }}
  >
    <div style={{
      width: '100%', aspectRatio: '1 / 1', background: '#f3f4f6',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <img src={img.url} alt={badge}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        onError={e => { e.currentTarget.style.opacity = 0.4; }} />
      {selected && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(220,38,38,0.18)',
          pointerEvents: 'none',
        }} />
      )}
      {selected && (
        <div style={{
          position: 'absolute', top: 8, right: 8,
          background: '#dc2626', color: '#fff',
          width: 28, height: 28, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 2px 6px rgba(220,38,38,0.4)',
        }}><X size={16} /></div>
      )}
    </div>
    <div style={{ padding: '10px 12px' }}>
      <span style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: 12,
        fontSize: 11, fontWeight: 600,
        background: badgeColor.bg, color: badgeColor.fg,
      }}>{badge}</span>
    </div>
  </div>
);

export default function ImageRemoveTab() {
  const [step, setStep] = useState(1);
  const [input, setInput] = useState('');
  const [fetching, setFetching] = useState(false);
  const [products, setProducts] = useState([]);
  // Markerade GUIDs per partNo: { [partNo]: { main: bool, additional: Set<key> } }
  const [selection, setSelection] = useState({});
  const [confirm1, setConfirm1] = useState(false);
  const [confirm2, setConfirm2] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [results, setResults] = useState([]);

  const fetchImages = async () => {
    const partNos = input.split(/[\s,;\n]+/).map(s => s.trim()).filter(Boolean);
    if (partNos.length === 0) {
      toast.error('Skriv in minst ett PartNo.');
      return;
    }
    setFetching(true);
    try {
      const r = await fetch(`${API}/api/images/product-images`, {
        method: 'POST', headers: await authHeaders(),
        body: JSON.stringify({ part_nos: partNos }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${(await r.text()).slice(0, 150)}`);
      const data = await r.json();
      setProducts(data.results || []);
      // Initiera selection (inget markerat från start)
      const init = {};
      (data.results || []).forEach(p => {
        init[p.part_no] = { main: false, additional: new Set() };
      });
      setSelection(init);
      setStep(2);
    } catch (e) {
      toast.error(`Hämtning misslyckades: ${e.message}`);
    } finally {
      setFetching(false);
    }
  };

  const toggleMain = (partNo) => {
    setSelection(prev => ({
      ...prev,
      [partNo]: { ...prev[partNo], main: !prev[partNo].main },
    }));
  };

  const toggleAdditional = (partNo, key) => {
    setSelection(prev => {
      const cur = new Set(prev[partNo].additional);
      if (cur.has(key)) cur.delete(key); else cur.add(key);
      return { ...prev, [partNo]: { ...prev[partNo], additional: cur } };
    });
  };

  // Räkna markerade
  const counts = useMemo(() => {
    let totalImages = 0, productsWithSelections = 0;
    products.forEach(p => {
      const sel = selection[p.part_no];
      if (!sel) return;
      let hit = 0;
      if (sel.main && p.main_image) hit += 1;
      hit += sel.additional.size;
      if (hit > 0) {
        productsWithSelections += 1;
        totalImages += hit;
      }
    });
    return { totalImages, productsWithSelections };
  }, [products, selection]);

  // Lista av alla markerade bilder för bekräftelsedialog
  const markedList = useMemo(() => {
    const arr = [];
    products.forEach(p => {
      const sel = selection[p.part_no];
      if (!sel) return;
      if (sel.main && p.main_image) {
        arr.push({ partNo: p.part_no, productName: p.product_name, kind: 'Huvudbild' });
      }
      (p.additional_images || []).forEach((img, i) => {
        if (sel.additional.has(img.key)) {
          arr.push({ partNo: p.part_no, productName: p.product_name, kind: `Extrabild ${i + 2}` });
        }
      });
    });
    return arr;
  }, [products, selection]);

  const submitRemoval = async () => {
    setConfirm1(false);
    setConfirm2(false);
    setRemoving(true);
    setStep(3);
    const res = [];
    for (const p of products) {
      const sel = selection[p.part_no];
      if (!sel) continue;
      const removeMain = !!(sel.main && p.main_image);
      const additionalImages = p.additional_images || [];
      const removedAdditional = additionalImages.filter(img => sel.additional.has(img.key));
      const touchAdditional = removedAdditional.length > 0;
      const keepAdditional = additionalImages
        .filter(img => !sel.additional.has(img.key))
        .map(img => ({ key: img.key, url: img.url }));

      if (!removeMain && !touchAdditional) continue;

      try {
        const r = await fetch(`${API}/api/images/remove-images`, {
          method: 'POST', headers: await authHeaders(),
          body: JSON.stringify({
            part_no: p.part_no,
            remove_main: removeMain,
            touch_additional: touchAdditional,
            keep_additional: keepAdditional,
          }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}: ${(await r.text()).slice(0, 150)}`);
        const data = await r.json();
        res.push({ ...data, removed_count: (removeMain ? 1 : 0) + removedAdditional.length });
      } catch (e) {
        res.push({ part_no: p.part_no, success: false, error: e.message });
      }
      setResults([...res]);
    }
    setRemoving(false);
    const ok = res.filter(r => r.success).length;
    toast.success(`${ok}/${res.length} produkter uppdaterade.`);
  };

  const reset = () => {
    setStep(1);
    setInput('');
    setProducts([]);
    setSelection({});
    setResults([]);
  };

  return (
    <div data-testid="image-remove-tab">
      {/* Stegindikator */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 18,
        padding: '16px 22px', background: '#fff', border: '1px solid #e2e8f0',
        borderRadius: 12, marginBottom: 28, boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <Step n={1} label="Ange PartNo" active={step === 1} done={step > 1} />
        <ArrowRight size={14} color="#cbd5e1" />
        <Step n={2} label="Välj bilder" active={step === 2} done={step > 2} />
        <ArrowRight size={14} color="#cbd5e1" />
        <Step n={3} label="Resultat" active={step === 3} done={false} />
      </div>

      {/* ─── STEG 1: ange PartNo ─── */}
      {step === 1 && (
        <div>
          <label className="form-label">PartNos (en per rad, eller komma-/mellanslagsseparerade)</label>
          <textarea
            data-testid="remove-input"
            className="form-input"
            rows={5}
            placeholder={'10001748-1077-10047\n10001045-1150-10036'}
            value={input}
            onChange={e => setInput(e.target.value)}
            style={{ fontFamily: 'monospace', fontSize: 13 }}
          />
          <div style={{ marginTop: 18 }}>
            <button
              className={`generate-btn${!fetching ? ' generate-btn--active' : ''}`}
              disabled={fetching}
              onClick={fetchImages}
              data-testid="btn-fetch-images"
            >
              {fetching
                ? <><span className="spinner" /> Hämtar bilder...</>
                : <><Search size={15} /> Hämta bilder</>}
            </button>
          </div>
        </div>
      )}

      {/* ─── STEG 2: välj bilder ─── */}
      {step === 2 && (
        <div>
          {products.map(p => {
            const sel = selection[p.part_no] || { main: false, additional: new Set() };
            const notFound = p.status !== 'ok';
            return (
              <div
                key={p.part_no}
                data-testid={`product-block-${p.part_no}`}
                style={{
                  background: '#f8fafc', border: '1px solid #e2e8f0',
                  borderRadius: 14, padding: 22, marginBottom: 22,
                }}
              >
                <div style={{ marginBottom: 16, display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                  <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0f172a' }}>
                    {p.product_name || 'Okänd produkt'}
                  </h3>
                  <code style={{ fontSize: 12, color: '#64748b', fontFamily: 'monospace' }}>
                    {p.part_no}
                  </code>
                  {notFound && (
                    <span style={{
                      background: '#fee2e2', color: '#991b1b',
                      padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                    }}>{p.error || 'Hittades inte'}</span>
                  )}
                </div>

                {!notFound && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 22 }}>
                    {/* Huvudbild */}
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.05 }}>
                        Huvudbild
                      </div>
                      {p.main_image ? (
                        <ImageCard
                          img={p.main_image}
                          badge="Huvudbild"
                          badgeColor={{ bg: '#dbeafe', fg: '#1e40af' }}
                          selected={sel.main}
                          onToggle={() => toggleMain(p.part_no)}
                        />
                      ) : (
                        <div style={{
                          background: '#fff', border: '1px dashed #cbd5e1',
                          borderRadius: 12, padding: 24, textAlign: 'center',
                          color: '#94a3b8', fontSize: 13,
                        }}>Ingen huvudbild</div>
                      )}
                    </div>

                    {/* Extrabilder */}
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.05 }}>
                        Extrabilder ({(p.additional_images || []).length})
                      </div>
                      {(p.additional_images || []).length > 0 ? (
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                          gap: 12,
                        }}>
                          {p.additional_images.map((img, i) => (
                            <ImageCard
                              key={img.key}
                              img={img}
                              badge={`Extrabild ${i + 2}`}
                              badgeColor={{ bg: '#e5e7eb', fg: '#374151' }}
                              selected={sel.additional.has(img.key)}
                              onToggle={() => toggleAdditional(p.part_no, img.key)}
                            />
                          ))}
                        </div>
                      ) : (
                        <div style={{
                          background: '#fff', border: '1px dashed #cbd5e1',
                          borderRadius: 12, padding: 24, textAlign: 'center',
                          color: '#94a3b8', fontSize: 13,
                        }}>Inga extrabilder</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <button className="btn-text" onClick={() => setStep(1)} data-testid="btn-back-1">
              <ArrowLeft size={14} /> Tillbaka
            </button>
            <div style={{ fontSize: 14, color: '#374151' }}>
              <strong>{counts.totalImages}</strong> bilder markerade på <strong>{counts.productsWithSelections}</strong> produkter
            </div>
            <button
              data-testid="btn-remove"
              disabled={counts.totalImages === 0 || removing}
              onClick={() => setConfirm1(true)}
              style={{
                background: counts.totalImages === 0 ? '#fca5a5' : '#dc2626',
                color: '#fff', border: 'none',
                padding: '12px 24px', borderRadius: 10,
                fontWeight: 600, fontSize: 14,
                cursor: counts.totalImages === 0 ? 'not-allowed' : 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 8,
                boxShadow: counts.totalImages > 0 ? '0 4px 14px rgba(220,38,38,0.35)' : 'none',
              }}
            >
              <Trash2 size={15} /> Ta bort markerade bilder
            </button>
          </div>
        </div>
      )}

      {/* ─── STEG 3: resultat ─── */}
      {step === 3 && (
        <div>
          {removing && (
            <div style={{ marginBottom: 16, fontWeight: 600 }}>
              Tar bort bilder... ({results.length}/{counts.productsWithSelections})
            </div>
          )}
          {results.map(r => (
            <div
              key={r.part_no}
              data-testid={`remove-result-${r.part_no}`}
              style={{
                padding: 16, marginBottom: 12,
                background: '#fff', border: `1px solid ${r.success ? '#bbf7d0' : '#fecaca'}`,
                borderRadius: 10,
                display: 'flex', alignItems: 'center', gap: 12,
              }}
            >
              {r.success
                ? <CheckCircle2 size={22} color="#10b981" />
                : <XCircle size={22} color="#dc2626" />}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{r.product_name || r.part_no}</div>
                <div style={{ fontSize: 12, color: '#64748b', fontFamily: 'monospace' }}>{r.part_no}</div>
                {r.error && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>{r.error}</div>}
                {r.success && r.removed_count != null && (
                  <div style={{ fontSize: 12, color: '#10b981', marginTop: 4 }}>
                    {r.removed_count} bild(er) borttagna
                  </div>
                )}
              </div>
            </div>
          ))}
          {!removing && (
            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button
                className="btn-secondary"
                onClick={fetchImages}
                data-testid="btn-refetch"
                disabled={fetching}
              >
                {fetching ? <Loader2 size={14} className="spin" /> : <RotateCcw size={14} />}
                Hämta bilder igen
              </button>
              <button
                className="generate-btn generate-btn--active"
                onClick={reset}
                data-testid="btn-reset"
                style={{ width: 'auto', minWidth: 200 }}
              >
                <ArrowLeft size={15} /> Börja om
              </button>
            </div>
          )}
        </div>
      )}

      {/* Bekräftelse 1 */}
      {confirm1 && (
        <ConfirmModal
          testid="confirm-1"
          title="Är du säker?"
          icon={<AlertTriangle size={22} color="#dc2626" />}
          body={
            <p>
              Du är på väg att ta bort <strong>{counts.totalImages}</strong> bilder från{' '}
              <strong>{counts.productsWithSelections}</strong> produkter i Norce <strong>Production</strong>.
              Detta kan inte ångras.
            </p>
          }
          onCancel={() => setConfirm1(false)}
          onConfirm={() => { setConfirm1(false); setConfirm2(true); }}
          confirmLabel="Fortsätt"
        />
      )}

      {/* Bekräftelse 2 */}
      {confirm2 && (
        <ConfirmModal
          testid="confirm-2"
          title="Slutgiltig bekräftelse"
          icon={<AlertTriangle size={22} color="#dc2626" />}
          body={
            <>
              <p>Följande bilder kommer raderas:</p>
              <ul style={{ fontSize: 13, color: '#374151', maxHeight: 250, overflowY: 'auto', margin: '12px 0', paddingLeft: 20 }}>
                {markedList.map((m, i) => (
                  <li key={i}>
                    <strong>{m.kind}</strong> — {m.productName || m.partNo}{' '}
                    <code style={{ fontSize: 11, color: '#64748b' }}>({m.partNo})</code>
                  </li>
                ))}
              </ul>
            </>
          }
          onCancel={() => setConfirm2(false)}
          onConfirm={submitRemoval}
          confirmLabel="Ja, ta bort"
        />
      )}
    </div>
  );
}

function ConfirmModal({ testid, title, icon, body, onCancel, onConfirm, confirmLabel }) {
  return (
    <div
      data-testid={testid}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', padding: 28, borderRadius: 14,
          maxWidth: 540, width: 'calc(100vw - 40px)',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          {icon}
          <h3 style={{ margin: 0, fontSize: 18 }}>{title}</h3>
        </div>
        <div style={{ color: '#374151' }}>{body}</div>
        <div style={{ marginTop: 22, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="btn-secondary" onClick={onCancel} data-testid={`${testid}-cancel`}>
            Avbryt
          </button>
          <button
            data-testid={`${testid}-confirm`}
            onClick={onConfirm}
            style={{
              background: '#dc2626', color: '#fff', border: 'none',
              padding: '10px 20px', borderRadius: 8, fontWeight: 600, cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(220,38,38,0.35)',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
